import { useRef, useEffect, useState } from 'react';

interface ScrollData {
  scrollY: number;
  scrollX: number;
  direction: 'up' | 'down';
  delta: number;
}

export function useScroll() {
  const [scrollData, setScrollData] = useState<ScrollData>({
    scrollY: 0,
    scrollX: 0,
    direction: 'down',
    delta: 0,
  });

  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      const delta = scrollY - lastScrollY.current;
      const direction = delta > 0 ? 'down' : 'up';

      setScrollData({
        scrollY,
        scrollX,
        direction,
        delta,
      });

      lastScrollY.current = scrollY;
    };

    const onScroll = () => {
      requestAnimationFrame(handleScroll);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return scrollData;
}

// Intersection Observer hook for reveal animations
export function useInView(options?: IntersectionObserverInit) {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          if (!options?.root) {
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.1, ...options }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [options]);

  return { ref, isInView };
}

// ---- Motion utilities (no external libs) ----

// Honors the user's OS-level "reduce motion" preference.
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  return reduced;
}

// Returns true when the device is a touch / coarse pointer (mobile / tablet).
export function useIsTouch(): boolean {
  const [touch, setTouch] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(hover: none), (pointer: coarse)');
    const update = () => setTouch(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return touch;
}
